import {driveBaseKey} from '@app/app-queries';
import {
  DRIVE_UPLOADS_PERSIST_KEY,
  driveUploadStore,
  useUploadPanel,
} from '@app/drive/uploading/drive-upload-store';
import {useAuth} from '@common/auth/use-auth';
import {queryClient} from '@common/http/query-client';
import {
  FileUploadProvider,
  useFileUploadStore,
  useUploadStoreLifecycle,
} from '@common/uploads/uploader/file-upload-provider';
import {
  CancelUploadDialog,
  progressValueText,
  UploadQueueItem,
} from '@common/uploads/uploader/ui/upload-queue-item';
import {
  shortDurationMessage,
  timeLeftMessage,
} from '@common/uploads/uploader/eta/upload-eta';
import {
  useOverallEta,
  useSpeedFormatter,
} from '@common/uploads/uploader/eta/use-upload-eta';
import {Button} from '@shadcn/button/button';
import {Tooltip} from '@shadcn/tooltip/tooltip';
import {useVirtualizer} from '@tanstack/react-virtual';
import {message} from '@ui/i18n/message';
import {MessageDescriptor} from '@ui/i18n/message-descriptor';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {openDialog} from '@ui/overlays/store/dialog-store';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {toast} from '@ui/toast/toast';
import {useBottomInset} from '@ui/utils/hooks/use-bottom-inset';
import {
  ChevronDownIcon,
  FileUpIcon,
  PauseIcon,
  PlayIcon,
  XIcon,
} from 'lucide-react';
import {ReactElement, useEffect, useMemo, useRef} from 'react';
import {useShallow} from 'zustand/react/shallow';

/**
 * Mounted once next to the router outlet: the Drive upload queue survives
 * every navigation (Drive, admin, account settings…) in this tab.
 */
export function GlobalUploadHost() {
  const {isLoggedIn} = useAuth();
  if (!isLoggedIn) return null;
  return <GlobalUploadHostInner />;
}

function GlobalUploadHostInner() {
  const store = driveUploadStore();
  useUploadStoreLifecycle(store, DRIVE_UPLOADS_PERSIST_KEY);

  useEffect(() => {
    let prevSize = store.getState().fileUploads.size;
    let prevCompleted = store.getState().completedUploadsCount;
    // Interrupted uploads restored on load: show them.
    if (prevSize > 0) useUploadPanel.getState().open();
    return store.subscribe(state => {
      if (state.fileUploads.size > prevSize) {
        const panel = useUploadPanel.getState();
        if (!panel.visible) panel.open();
      }
      if (state.completedUploadsCount > prevCompleted) {
        // Also covers uploads resumed after a reload (callbacks are not persisted).
        queryClient.invalidateQueries({queryKey: driveBaseKey});
        queryClient.invalidateQueries({queryKey: ['storage-summary']});
      }
      prevSize = state.fileUploads.size;
      prevCompleted = state.completedUploadsCount;
    });
  }, [store]);

  return (
    <FileUploadProvider store={store}>
      <UploadQueue />
    </FileUploadProvider>
  );
}

export function UploadQueue() {
  const visible = useUploadPanel(s => s.visible);
  const expanded = useUploadPanel(s => s.expanded);
  const hasUploads = useFileUploadStore(s => s.fileUploads.size > 0);
  const ref = useRef<HTMLDivElement>(null);
  const shown = visible && hasUploads;
  useBottomInset(ref, 'upload-dock', shown);

  if (!shown) return null;

  return (
    <div
      ref={ref}
      className="fixed end-4 bottom-[calc(1rem+var(--overlay-inset-bottom,0px))] z-50 max-w-[calc(100vw-2rem)] transition-[bottom] duration-200"
      data-testid="upload-dock"
    >
      {expanded ? <ExpandedPanel /> : <UploadPill />}
    </div>
  );
}

function useOverallProgress() {
  return useFileUploadStore(
    useShallow(s => {
      let total = 0;
      let done = 0;
      s.fileUploads.forEach(u => {
        if (u.status === 'interrupted') return;
        total += u.file.size;
        done +=
          u.status === 'completed'
            ? u.file.size
            : Math.min(u.bytesUploaded || 0, u.file.size);
      });
      return {
        percentage: total ? Math.floor((done / total) * 100) : 0,
        active: s.activeUploadsCount,
        paused: s.pausedUploadsCount,
        failed: s.failedUploadsCount,
        interrupted: s.interruptedUploadsCount,
        completed: s.completedUploadsCount,
      };
    }),
  );
}

function SummaryText() {
  const {active, paused, failed, interrupted, completed} = useOverallProgress();
  if (active) {
    return (
      <Trans
        message="[one Uploading 1 file|other Uploading :count files]"
        values={{count: active}}
      />
    );
  }
  if (interrupted) {
    return (
      <Trans
        message="[one 1 interrupted upload|other :count interrupted uploads]"
        values={{count: interrupted}}
      />
    );
  }
  if (paused) {
    return (
      <Trans
        message="[one 1 upload paused|other :count uploads paused]"
        values={{count: paused}}
      />
    );
  }
  if (failed) {
    return (
      <Trans
        message="[one 1 upload failed|other :count uploads failed]"
        values={{count: failed}}
      />
    );
  }
  if (completed) {
    return (
      <Trans
        message="[one Uploaded 1 file|other Uploaded :count files]"
        values={{count: completed}}
      />
    );
  }
  return <Trans message="No active uploads" />;
}

function UploadPill() {
  const expand = useUploadPanel(s => s.expand);
  const {percentage, active} = useOverallProgress();
  const {trans} = useTrans();
  const eta = useOverallEta(active > 0);
  const short = eta?.seconds != null ? shortDurationMessage(eta.seconds) : null;
  return (
    <button
      type="button"
      onClick={() => expand()}
      data-testid="upload-pill"
      aria-label={trans(message('Show uploads'))}
      className="flex max-w-full items-center gap-2.5 rounded-full border bg-background py-1.5 ps-2 pe-4 text-sm font-medium shadow-xl transition-colors hover:bg-hover"
    >
      <ProgressCircle
        aria-label={trans(message('Upload progress'))}
        size="size-7"
        value={percentage}
        trackWidth={3}
        valueText={progressValueText(
          trans,
          percentage,
          eta?.seconds ?? null,
          active > 0 && eta?.seconds == null,
        )}
      />
      <span className="min-w-0 truncate">
        <SummaryText />
      </span>
      {active ? (
        <span
          className="flex shrink-0 items-center text-muted-foreground tabular-nums"
          data-testid="upload-pill-eta"
        >
          <bdi dir="ltr">{percentage}%</bdi>
          {short ? (
            <>
              <span className="px-1">·</span>
              <span>
                <Trans {...short} />
              </span>
            </>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}

function HeaderButton({
  label,
  icon,
  onClick,
  testId,
}: {
  label: MessageDescriptor;
  icon: ReactElement;
  onClick: () => void;
  testId: string;
}) {
  const {trans} = useTrans();
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label={trans(label)}
            data-testid={testId}
            onClick={onClick}
          />
        }
      >
        {icon}
      </Tooltip.Trigger>
      <Tooltip.Content>
        <Trans {...label} />
      </Tooltip.Content>
    </Tooltip>
  );
}

function ExpandedPanel() {
  return (
    <div
      className="w-md max-w-[calc(100vw-2rem)] animate-in overflow-hidden rounded-card-sm border bg-background text-sm shadow-xl duration-300 fade-in-0 slide-in-from-bottom-1"
      data-testid="upload-queue"
    >
      <Header />
      <OverallProgress />
      <InterruptedBanner />
      <UploadList />
    </div>
  );
}

export function Header() {
  const {active, paused, failed, interrupted, completed} = useOverallProgress();
  const pauseAll = useFileUploadStore(s => s.pauseAll);
  const resumeAll = useFileUploadStore(s => s.resumeAll);
  const cancelAll = useFileUploadStore(s => s.cancelAll);
  const clearInactive = useFileUploadStore(s => s.clearInactive);
  const {minimize, close} = useUploadPanel.getState();
  const unfinished = active + paused + failed + interrupted;

  return (
    <div className="flex min-h-11 items-center justify-between gap-1 border-b py-1 ps-3 pe-1.5 font-medium">
      <div className="min-w-0 flex-auto truncate" data-testid="upload-queue-title">
        <SummaryText />
      </div>
      {active > 0 ? (
        <HeaderButton
          label={message('Pause all')}
          icon={<PauseIcon className="size-4" />}
          onClick={() => pauseAll()}
          testId="upload-pause-all"
        />
      ) : null}
      {paused + failed > 0 ? (
        <HeaderButton
          label={message('Resume all')}
          icon={<PlayIcon className="size-4" />}
          onClick={() => resumeAll()}
          testId="upload-resume-all"
        />
      ) : null}
      {unfinished > 1 ? (
        <HeaderButton
          label={message('Cancel all')}
          icon={<XIcon className="size-4 text-destructive" />}
          onClick={async () => {
            const confirmed = await openDialog(CancelUploadDialog, {
              count: unfinished,
            });
            if (confirmed) await cancelAll();
          }}
          testId="upload-cancel-all"
        />
      ) : null}
      <HeaderButton
        label={message('Minimize')}
        icon={<ChevronDownIcon className="size-4" />}
        onClick={() => minimize()}
        testId="upload-minimize"
      />
      {active === 0 && interrupted === 0 && (completed > 0 || failed + paused === 0) ? (
        <HeaderButton
          label={message('Close')}
          icon={<XIcon className="size-4" />}
          onClick={() => {
            close();
            // wait for the panel animation, then clear finished rows
            setTimeout(() => clearInactive(), 200);
          }}
          testId="upload-close"
        />
      ) : null}
    </div>
  );
}

function OverallProgress() {
  const {percentage, active, paused} = useOverallProgress();
  const eta = useOverallEta(active > 0);
  const formatSpeed = useSpeedFormatter();
  const {trans} = useTrans();
  if (!active && !paused) return null;
  const left =
    active > 0
      ? eta?.seconds != null
        ? timeLeftMessage(eta.seconds)
        : {message: 'Estimating…'}
      : null;
  return (
    <div className="border-b px-3 py-2" data-testid="upload-overall">
      <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span
          className="min-w-0 truncate"
          data-testid="upload-overall-eta"
          data-eta-seconds={
            eta?.seconds != null ? Math.round(eta.seconds) : undefined
          }
        >
          {left ? (
            <Trans {...left} />
          ) : (
            <Trans message="Overall progress" />
          )}
        </span>
        <span className="flex shrink-0 items-center tabular-nums">
          {active > 0 && eta?.speed ? (
            <>
              <bdi dir="ltr" data-testid="upload-overall-speed">
                {formatSpeed(eta.speed)}
              </bdi>
              <span className="px-1">·</span>
            </>
          ) : null}
          <bdi dir="ltr">{percentage}%</bdi>
        </span>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={trans(message('Overall progress'))}
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={progressValueText(
          trans,
          percentage,
          eta?.seconds ?? null,
          active > 0 && eta?.seconds == null,
        )}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{width: `${percentage}%`}}
        />
      </div>
    </div>
  );
}

function InterruptedBanner() {
  const interrupted = useFileUploadStore(s => s.interruptedUploadsCount);
  const reattachFiles = useFileUploadStore(s => s.reattachFiles);
  const cancelUpload = useFileUploadStore(s => s.cancelUpload);
  const fileUploads = useFileUploadStore(s => s.fileUploads);
  const inputRef = useRef<HTMLInputElement>(null);
  if (!interrupted) return null;
  return (
    <div
      className="flex flex-col gap-2 border-b bg-warning/8 px-3 py-2.5 text-xs"
      data-testid="upload-interrupted-banner"
    >
      <p className="text-foreground">
        <Trans message="These uploads stopped when the page was closed. Select the same files again to continue where they left off." />
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="xs"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          data-testid="upload-reselect-all"
        >
          <FileUpIcon className="size-3.5" />
          <Trans message="Select files to resume" />
        </Button>
        <Button
          size="xs"
          variant="ghost"
          onClick={async () => {
            const ids = [...fileUploads.values()]
              .filter(u => u.status === 'interrupted')
              .map(u => u.file.id);
            const confirmed = await openDialog(CancelUploadDialog, {
              count: ids.length,
              interrupted: true,
            });
            if (confirmed) await Promise.all(ids.map(id => cancelUpload(id)));
          }}
          data-testid="upload-discard-all"
        >
          <Trans message="Discard" />
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        data-testid="upload-reselect-all-input"
        onChange={e => {
          const files = e.target.files ? [...e.target.files] : [];
          e.target.value = '';
          if (!files.length) return;
          const {matched, unmatched} = reattachFiles(files);
          if (unmatched.length) {
            toast.danger(
              message(
                '[one 1 file did not match an interrupted upload (name, size and date must be the same).|other :count files did not match an interrupted upload (name, size and date must be the same).]',
                {values: {count: unmatched.length}},
              ),
            );
          } else if (matched) {
            toast(
              message('[one Resuming 1 upload.|other Resuming :count uploads.]', {
                values: {count: matched},
              }),
            );
          }
        }}
      />
    </div>
  );
}

function UploadList() {
  const uploads = useFileUploadStore(s => s.fileUploads);
  const uploadsArray = useMemo(() => [...uploads.values()], [uploads]);
  const ref = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: uploads.size,
    getScrollElement: () => ref.current,
    estimateSize: () => 60,
    overscan: 4,
  });

  return (
    <div className="max-h-[min(20rem,50dvh)] overflow-y-auto" ref={ref}>
      <div
        className="relative w-full"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map(virtualItem => {
          const upload = uploadsArray[virtualItem.index];
          return (
            <UploadQueueItem
              key={upload.file.id}
              file={upload.file}
              className="absolute start-0 top-0 w-full py-3 ps-3 pe-2"
              style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
